import { CarouselDrag } from "@theme/carousel-drag";

/*
 * <product-media>
 * CSS scroll-snap carousel for product media. Layout and swiping are native; this element only
 * syncs the pagination/arrows with the visible slide, loads deferred media (video, external video,
 * 3D) on demand and pauses media that scrolls out of view. With a mouse, the slider can be dragged
 * (carousel-drag.js). With `data-desktop-grid`, CSS lays the slides out
 * as a grid from 750px up and dragging is turned off there.
 *
 * The block is re-rendered on variant change (`data-variant-update`), leaving out media attached to
 * the other variants and leading with the selected variant's media. The variant picker hands the
 * rendered element to `updateFrom()`, which keeps the slides both versions share (loaded images and
 * videos included) and fades the slider while the slide set changes. Dawn's pubsub `variantChange`
 * scrolls to the variant's featured media instead.
 *
 * Public API:
 *   element.goTo(indexOrMediaId, { instant })
 *   element.updateFrom(renderedProductMedia)
 *   'product-media:change' event -> detail: { index, slide }
 */
if (!customElements.get("product-media")) {
  class ProductMedia extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        slider: "[data-slider]",
        slide: "[data-slide]",
        dot: "[data-dot]",
        prevButton: "[data-prev]",
        nextButton: "[data-next]",
        deferredPoster: "[data-deferred-poster]",
        deferredMedia: "[data-deferred-media]",
      };
    }

    connectedCallback() {
      this.slider = this.querySelector(this.selectors.slider);
      this.slides = [...this.querySelectorAll(this.selectors.slide)];
      this.cacheControls();
      this.currentIndex = 0;
      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      );
      this.abortController = new AbortController();
      this.gridQuery = this.hasAttribute("data-desktop-grid")
        ? window.matchMedia("(min-width: 750px)")
        : null;
      this.gridQuery?.addEventListener("change", () => this.updateDrag(), {
        signal: this.abortController.signal,
      });

      this.addEventListener("click", this.onClick.bind(this), {
        signal: this.abortController.signal,
      });

      this.setupSlider();

      if (
        typeof subscribe === "function" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        this.unsubscribeVariantChange = subscribe(
          PUB_SUB_EVENTS.variantChange,
          ({ data }) => {
            const mediaId = data?.variant?.featured_media?.id;
            if (mediaId) this.goTo(mediaId);
          },
        );
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
      this.observer = null;
      this.drag = null;
      this.unsubscribeVariantChange?.();
    }

    cacheControls() {
      this.dots = [...this.querySelectorAll(this.selectors.dot)];
      this.prevButton = this.querySelector(this.selectors.prevButton);
      this.nextButton = this.querySelector(this.selectors.nextButton);
    }

    /**
     * Slide tracking and mouse dragging, set up once the slider holds more than one slide.
     */
    setupSlider() {
      if (this.slides.length < 2) return;

      if (!this.observer)
        this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
          root: this.slider,
          threshold: 0.5,
        });
      this.slides.forEach((slide) => this.observer.observe(slide));

      if (!this.drag)
        this.drag = new CarouselDrag(this.slider, {
          signal: this.abortController.signal,
          getPositions: () => this.slides.map((slide) => slide.offsetLeft),
          scrollTo: (left) =>
            this.slider.scrollTo({
              left,
              behavior: this.reducedMotion.matches ? "instant" : "smooth",
            }),
        });
      this.updateDrag();
    }

    /**
     * The grid layout has nothing to drag.
     */
    updateDrag() {
      if (this.drag) this.drag.enabled = !this.gridQuery?.matches;
    }

    /**
     * Takes over the content of a freshly rendered <product-media> (variant change) without
     * replacing the element. Badges and controls are swapped as they are. When the slide set is
     * unchanged the slider glides back to the first slide; otherwise it fades out, keeps the
     * slides it already has, adopts the new ones and fades back in once the first image decodes.
     */
    async updateFrom(next) {
      const token = (this.updateToken = {});
      const nextSlider = next.querySelector(this.selectors.slider);
      const nextSlides = [...nextSlider.querySelectorAll(this.selectors.slide)];
      const mediaIds = (slides) =>
        slides.map((slide) => slide.dataset.mediaId).join();
      const animate = !this.reducedMotion.matches;

      if (mediaIds(nextSlides) === mediaIds(this.slides)) {
        this.replaceControls(next, nextSlider);
        this.updateControls(this.currentIndex);
        // An earlier update may have faded the slider out
        if (animate) this.fadeSlider(1);
        return this.goTo(0);
      }

      if (animate) await this.fadeSlider(0);
      if (token !== this.updateToken || !this.isConnected) return;

      const slides = nextSlides.map((nextSlide) => {
        const slide =
          this.slides.find(
            (current) => current.dataset.mediaId === nextSlide.dataset.mediaId,
          ) ?? nextSlide;
        slide.setAttribute("aria-label", nextSlide.getAttribute("aria-label"));
        return slide;
      });

      this.slides
        .filter((slide) => !slides.includes(slide))
        .forEach((slide) => this.pauseMedia(slide));
      this.observer?.disconnect();
      this.slider.replaceChildren(...slides);
      this.slides = slides;
      this.replaceControls(next, nextSlider);
      this.setupSlider();

      this.slider.scrollTo({ left: 0, behavior: "instant" });
      this.currentIndex = 0;
      this.updateControls(0);

      if (!animate) return;

      // Fade in on the new first image, without stalling on a slow network
      const image = slides[0]?.querySelector("img");
      await Promise.race([
        image?.decode().catch(() => {}),
        new Promise((resolve) => setTimeout(resolve, 300)),
      ]);
      if (token === this.updateToken) this.fadeSlider(1);
    }

    /**
     * Swaps everything but the slider (badges, dots, arrows) with the rendered element's.
     */
    replaceControls(next, nextSlider) {
      [...this.children]
        .filter((child) => child !== this.slider)
        .forEach((child) => child.remove());
      this.append(
        ...[...next.children].filter((child) => child !== nextSlider),
      );
      this.cacheControls();
    }

    fadeSlider(opacity) {
      return this.slider
        .animate([{ opacity }], {
          duration: opacity ? 250 : 150,
          easing: "ease",
          fill: "forwards",
        })
        .finished.catch(() => {});
    }

    /**
     * Scrolls to a slide by its index (number) or Shopify media id (string).
     * Unknown media ids are ignored, so variant events from other products are harmless.
     */
    goTo(target, { instant = false } = {}) {
      const index =
        typeof target === "number"
          ? target
          : this.slides.findIndex(
              (slide) => slide.dataset.mediaId === String(target),
            );
      const slide = this.slides[index];
      if (!slide) return;

      this.slider.scrollTo({
        left: slide.offsetLeft,
        behavior: instant || this.reducedMotion.matches ? "instant" : "smooth",
      });
    }

    onClick(event) {
      const dot = event.target.closest("[data-dot]");
      if (dot) return this.goTo(this.dots.indexOf(dot));
      if (event.target.closest("[data-prev]"))
        return this.goTo(this.currentIndex - 1);
      if (event.target.closest("[data-next]"))
        return this.goTo(this.currentIndex + 1);

      const poster = event.target.closest(this.selectors.deferredPoster);
      if (poster)
        return this.loadMedia(poster.closest(this.selectors.deferredMedia));

      // Native controls are hidden, so a click on the video toggles playback
      const video = event.target.closest("video");
      if (video) video.paused ? video.play() : video.pause();
    }

    onIntersect(entries) {
      const visible = entries.find((entry) => entry.isIntersecting);
      if (visible) this.setCurrent(this.slides.indexOf(visible.target));
    }

    setCurrent(index) {
      if (index === this.currentIndex) return;

      this.pauseMedia(this.slides[this.currentIndex]);
      this.currentIndex = index;
      this.updateControls(index);

      this.dispatchEvent(
        new CustomEvent("product-media:change", {
          bubbles: true,
          detail: { index, slide: this.slides[index] },
        }),
      );
    }

    updateControls(index) {
      this.dots.forEach((dot, i) =>
        dot.setAttribute("aria-current", i === index),
      );
      if (this.prevButton) this.prevButton.disabled = index === 0;
      if (this.nextButton)
        this.nextButton.disabled = index === this.slides.length - 1;
    }

    loadMedia(container) {
      if (!container || container.hasAttribute("data-loaded")) return;

      const content = container
        .querySelector("template")
        .content.firstElementChild.cloneNode(true);
      container.setAttribute("data-loaded", "");
      container.appendChild(content);

      const media = container.querySelector("video, iframe, model-viewer");
      if (!media) return;

      if (media.nodeName === "MODEL-VIEWER") {
        window.Shopify?.loadFeatures([
          {
            name: "model-viewer-ui",
            version: "1.0",
            onLoad: (error) => {
              if (error) return;
              container.modelViewerUI = new Shopify.ModelViewerUI(media);
            },
          },
        ]);
      }

      media.focus();
      // Safari ignores the autoplay attribute on injected videos
      if (media.nodeName === "VIDEO") media.play();
    }

    pauseMedia(slide) {
      if (!slide) return;

      slide.querySelector("video")?.pause();
      slide
        .querySelector(".js-youtube")
        ?.contentWindow.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          "*",
        );
      slide
        .querySelector(".js-vimeo")
        ?.contentWindow.postMessage('{"method":"pause"}', "*");
      slide.querySelector(this.selectors.deferredMedia)?.modelViewerUI?.pause();
    }
  }

  customElements.define("product-media", ProductMedia);
}
